// PCOSRiskScreening.jsx
// Production-ready PCOS Risk Screening
// Uses: ScreeningLayout.jsx (shared shell) + pcosScreeningData.js (JSON config)
import React, { useRef, useState } from 'react';
import {
  AppShell, Card, CardHeader, CardBody, CardFooter,
  ProgressBar, Sidebar,
  Logo, ChevronRight, ChevronLeft, Check, ReviewRow,
  ToggleGroup, ConcernGrid,
  ResultGauge, ConfidenceBar, NextActionList,
} from './ScreeningLayout';
import styles from './screening.module.css';
import {
  PCOS_STEPS, PCOS_FLOW,
  PCOS_INTRO, PCOS_CONSENT, PCOS_CONCERNS,
  PCOS_QUESTION_SCREENS, PCOS_REVIEW_SECTIONS,
  computePCOSScore, getPCOSRisk,
} from './pcosScreeningData';
// import { ConsentScreen } from '../../pages/CareNavigator';
import PregnancyWelcomeWeb from '../PregnancyRiskScreening/PregWelcomeScreen';
import { RISK_SCREENING_CONSENT } from './MentalWellnessScreening';
import ConsentScreen from '../Reusablecomponents/Consentscreen/Consentscreen';

// ── Illustrations ──────────────────────────────────────────────────────────────
function PCOSIllustration() {
  return (
    <svg viewBox="0 0 260 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.illusSvg}>
      <circle cx="155" cy="130" r="100" fill="#F3E8FF" opacity=".55" />
      <ellipse cx="130" cy="120" rx="45" ry="35" fill="#E879F9" opacity=".2" />
      <path d="M105 120C105 103 118 90 130 90C142 90 155 103 155 120C155 135 145 148 130 152C115 148 105 135 105 120Z" fill="#C026D3" opacity=".8" />
      <path d="M105 120Q95 105 85 108Q78 115 82 125Q88 132 98 128" stroke="#A21CAF" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M155 120Q165 105 175 108Q182 115 178 125Q172 132 162 128" stroke="#A21CAF" strokeWidth="3" fill="none" strokeLinecap="round" />
      <ellipse cx="82" cy="128" rx="12" ry="9" fill="#E879F9" opacity=".7" />
      <ellipse cx="178" cy="128" rx="12" ry="9" fill="#E879F9" opacity=".7" />
      <circle cx="76" cy="125" r="3" fill="#F0ABFC" />
      <circle cx="84" cy="122" r="2" fill="#F0ABFC" />
      <circle cx="80" cy="133" r="2.5" fill="#F0ABFC" />
      <circle cx="172" cy="125" r="3" fill="#F0ABFC" />
      <circle cx="180" cy="122" r="2" fill="#F0ABFC" />
      <circle cx="176" cy="133" r="2.5" fill="#F0ABFC" />
      <circle cx="130" cy="200" r="20" fill="#FBCFE8" />
      <path d="M118 195Q122 182 136 182Q148 186 144 200" fill="#9D174D" opacity=".9" />
      <path d="M108 240Q112 218 130 216Q148 218 152 240" fill="#BE185D" opacity=".85" />
      <path d="M108 240Q102 252 115 254Q130 255 145 254Q158 252 152 240" fill="#FBCFE8" opacity=".9" />
      <path d="M112 224Q95 232 90 244" stroke="#FBCFE8" strokeWidth="5" strokeLinecap="round" fill="none" />
      <ellipse cx="55" cy="258" rx="14" ry="8" fill="#86EFAC" transform="rotate(-30 55 258)" />
      <ellipse cx="65" cy="250" rx="10" ry="6" fill="#4ADE80" transform="rotate(20 65 250)" />
      <rect x="58" y="258" width="8" height="14" rx="2" fill="#FBCFE8" />
    </svg>
  );
}

// ── Consent illustration ────────────────────────────────────────────────────────
function ConsentIllustration() {
  return (
    <svg viewBox="0 0 80 80" fill="none" className={styles.consentIllusSvg}>
      <rect x="15" y="10" width="50" height="60" rx="6" fill="#EDE9FE" stroke="#7C3AED" strokeWidth="1.5" />
      <rect x="22" y="22" width="36" height="4" rx="2" fill="#7C3AED" opacity=".4" />
      <rect x="22" y="32" width="28" height="4" rx="2" fill="#7C3AED" opacity=".3" />
      <rect x="22" y="42" width="32" height="4" rx="2" fill="#7C3AED" opacity=".3" />
      <circle cx="58" cy="58" r="14" fill="#7C3AED" />
      <path d="M52 58L56 62L64 54" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUB-SCREENS
// ══════════════════════════════════════════════════════════════════════════════

function IntroScreen({ config, onStart }) {
  return (
    <CardBody>
      <div className={styles.introWrap}>
        <div className={styles.introContent}>
          <span className={styles.introBadge}>
            <span>{config.badgeIcon}</span> {config.badge}
          </span>
          <h1 className={styles.introHeading}>{config.heading}</h1>
          <p className={styles.introDesc}>{config.desc}</p>
          <ul className={styles.introBullets}>
            {config.bullets.map((b) => (
              <li key={b.text} className={styles.introBullet}>
                <span className={styles.introBulletIcon}>{b.icon}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles.introPrivacy}>🛡️ {config.privacy}</div>
          <button className={styles.introStartBtn} onClick={onStart}>
            {config.startLabel} <ChevronRight />
          </button>
          <button className={styles.introLaterBtn}>{config.laterLabel}</button>
        </div>
        <div className={styles.introIllus}>
          <div className={styles.illusBlob} />
          <PCOSIllustration />
        </div>
      </div>
    </CardBody>
  );
}

// function ConsentScreen({ config, onAgree, onBack }) {
//   const [checked, setChecked] = useState(false);
//   return (
//     <CardBody>
//       <div className={styles.consentWrap}>
//         <div className={styles.consentIllusWrap}><ConsentIllustration /></div>
//         <h2 className={styles.consentHeading}>{config.heading}</h2>
//         {config.paragraphs.map((p, i) => (
//           <p key={i} className={styles.consentText}>{p}</p>
//         ))}
//         <button
//           className={`${styles.consentCheckBtn} ${checked ? styles.consentCheckBtnActive : ''}`}
//           onClick={() => setChecked((v) => !v)}
//         >
//           <span className={styles.consentBox}>{checked && <Check />}</span>
//           <span>{config.checkLabel}</span>
//         </button>
//         <button className={styles.consentAgreeBtn} disabled={!checked} onClick={onAgree}>
//           {config.agreeLabel} <ChevronRight />
//         </button>
//       </div>
//     </CardBody>
//   );
// }

function ConcernsScreen({ concerns, concerns_config, selected, onChange, onNext, onBack }) {
  return (
    <>
      <CardBody>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>1</span> Main Concerns
        </div>
        <h2 className={styles.screenTitle}>What are your main concerns?</h2>
        <p className={styles.screenSubtitle}>Select all that apply</p>
        <ConcernGrid options={concerns_config} selected={selected} onChange={onChange} />
      </CardBody>
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Continue" disabled={selected.length === 0} />
    </>
  );
}

function QuestionScreen({ screenConfig, answers, onChange, onNext, onBack, questionIndex, totalQuestions, qIdxMap }) {
  // Show questions one at a time within each screen section
  const screenId = screenConfig.id;

 const [qIdx, setQIdx] = useState(
    () => qIdxMap?.current?.[screenId] ?? 0
  );

 const updateQIdx = (newIdx) => {
    setQIdx(newIdx);
    if (qIdxMap?.current) qIdxMap.current[screenId] = newIdx;
  };
  // Filter visible questions (handle conditionals)
  const visible = screenConfig.questions.filter((q) => {
    if (!q.conditional) return true;
    const { field, value, notValue } = q.conditional;
    const fv = answers[field];
    if (value !== undefined) return fv === value;
    if (notValue !== undefined) return fv !== notValue;
    return true;
  });

  const current = visible[qIdx];
  const isLast = qIdx === visible.length - 1;
  const progress = questionIndex + (qIdx / visible.length);

 const handleNext = () => { if (!isLast) { updateQIdx(qIdx + 1); return; } onNext(); };
  const handleBack = () => { if (qIdx > 0) { updateQIdx(qIdx - 1); return; } onBack(); };

  const hasAnswer = !!answers[current?.id];

  return (
    <>
      <ProgressBar 
      // step={progress + 1} 
      // total={totalQuestions} 
      step={qIdx}
        total={visible.length}
      label={`${screenConfig.stepLabel}`} />
      <CardBody>

        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>{screenConfig.icon}</span>
          {screenConfig.stepLabel}
        </div>
        <div className={styles.questionCounter}>
          <span className={styles.questionCountCurrent}>Question: {qIdx + 1}</span>
          <span className={styles.questionCountSep}>/</span>
          <span className={styles.questionCountTotal}>{visible.length}</span>
        </div>
        {/* <h2 className={styles.screenTitle}>{screenConfig.title}</h2> */}
        {qIdx === 0 && <p className={styles.screenSubtitle}>{screenConfig.subtitle}</p>}

        {current && (
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>{current.label}</label>
            <ToggleGroup
              options={current.options}
              value={answers[current.id]}
              onChange={(v) => onChange(current.id, v)}
            />
          </div>
        )}
      </CardBody>
      <CardFooter
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={isLast ? 'Continue' : 'Next'}
        disabled={!hasAnswer}
      />
    </>
  );
}

function ReviewScreen({ answers, sections, onEdit, onNext, onBack }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* <CardBody> */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px" }} >
      <div className={styles.screenStepBadge}>
        <span className={styles.screenStepNum}>📋</span> Review
      </div>
      <h2 className={styles.screenTitle}>Review your answers</h2>
      <p className={styles.screenSubtitle}>You can edit your responses before submitting</p>
      <div className={styles.reviewList}>
        {sections.map((sec) => {
          const vals = sec.fields.map((f) => answers[f]).filter(Boolean);
          return (
            <ReviewRow
              key={sec.id}
              icon={sec.icon}
              label={sec.label}
              value={vals.length ? vals.join(' · ') : '—'}
              onEdit={() => onEdit(sec.id)}
            />
          );
        })}
      </div>
      </div>
      {/* </CardBody> */}
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Show my result" />
    </div>
  );
}

function ResultScreen({ answers, onRestart }) {
  const pct = computePCOSScore(answers);
  const risk = getPCOSRisk(pct);
  const conf = Math.min(76 + Math.round(pct * 0.12), 94);

  return (
    <div className={styles.cardBodyFull}>
      <h2 className={styles.screenTitle}>Your PCOS Risk Result</h2>
      <p className={styles.resultDisclaimer}>This is not a medical diagnosis.</p>

      <ResultGauge
        pct={pct}
        riskLevel={risk.level}
        riskColor={risk.color}
        riskBg={risk.bg}
        riskBorder={risk.border}
        riskEmoji={risk.emoji}
        riskDesc={risk.desc}
      />
      <ConfidenceBar pct={conf} color={risk.color} />

      <div className={styles.resultMeansCard}>
        <p className={styles.resultSectionTitle}>What this means</p>
        <p className={styles.resultMeansText}>{risk.means}</p>
      </div>

      <p className={styles.resultSectionTitle}>Recommended for you</p>
      <NextActionList actions={risk.actions} />

      <button className={styles.resultPrimaryBtn} onClick={onRestart}>
        Start a new screening <ChevronRight />
      </button>
      <button className={styles.resultSecondaryBtn}>Back to dashboard</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR
// ══════════════════════════════════════════════════════════════════════════════
const QUESTION_SCREEN_IDS = PCOS_QUESTION_SCREENS.map((s) => s.id);

export default function PCOSRiskScreening() {
  const [flowIndex, setFlowIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [concerns, setConcerns] = useState([]);
const qIdxMapRef = useRef({});
  const currentId = PCOS_FLOW[flowIndex];
  // const goNext = () => setFlowIndex((i) => Math.min(i + 1, PCOS_FLOW.length - 1));
  const goNext = () => {
  setFlowIndex((i) => {
    const nextIndex = Math.min(i + 1, PCOS_FLOW.length - 1);
    const nextId = PCOS_FLOW[nextIndex];
    // Reset the next screen's question index so it always starts from Q1
    if (qIdxMapRef.current[nextId] !== undefined) {
      qIdxMapRef.current[nextId] = 0;
    }
    return nextIndex;
  });
};
  const goBack = () => setFlowIndex((i) => Math.max(i - 1, 0));

  const handleAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));

  // const handleEditSection = (sectionId) => {
  //   const idx = PCOS_FLOW.indexOf(sectionId);
  //   if (idx !== -1) setFlowIndex(idx);
  // };
const handleEditSection = (sectionId) => {
  const idx = PCOS_FLOW.indexOf(sectionId);
  if (idx !== -1) {
    qIdxMapRef.current[sectionId] = 0; // ← reset to first question
    setFlowIndex(idx);
  }
};
  const handleRestart = () => {
    setFlowIndex(0);
    setAnswers({});
    setConcerns([]);
  };

  // Sidebar step index
  const sidebarIndex = PCOS_STEPS.findIndex((s) => s.id === currentId);

  // Question screen index (for progress bar)
  const qScreenIdx = QUESTION_SCREEN_IDS.indexOf(currentId);
  const isQuestion = qScreenIdx !== -1;

  const showHeader = currentId !== 'intro';
  const showSidebar = !['intro', 'consent', 'result'].includes(currentId);
  const content = {
    "title": "Risk Screening Personalization",
    "description": "Use my answers to personalize my health risk screening.",
    "pageSubtitle": "Let's find or create your profile so we can personalize your risk screening.",
  }

  return (
    <AppShell accent="accentPurple">
      <Card>
        {showHeader && (
          <CardHeader
            stepLabel={currentId === 'result' ? null : PCOS_STEPS.find((s) => s.id === currentId)?.label}
            onExit={currentId !== 'result' ? handleRestart : undefined}
          />
        )}
        {!showHeader && <div style={{ padding: '18px 24px 0' }}><Logo /></div>}

        {/* {currentId === 'intro' && (
          <IntroScreen config={PCOS_INTRO} onStart={goNext} />
        )} */}

        {currentId === 'intro' && (
          <PregnancyWelcomeWeb
            onNext={goNext}
            eyebrow="PCOS Health"
            headlineAccent="hormonal health"
            bodyText="Answer a few questions to help us identify signs of PCOS and guide you toward the right next steps for your hormonal wellbeing."
            // illustration={<PCOSWomanSVG />}
          // onNext={handleNext}
          />
        )}

        {/* {currentId === 'consent' && (
          <ConsentScreen config={PCOS_CONSENT} onAgree={goNext} onBack={goBack} />
        )} */}
        {currentId === 'consent' &&
          <div className={styles.consentLayout}>
            <ConsentScreen
              config={RISK_SCREENING_CONSENT}
              // styles={cardstyles}
              onNext={goNext}
              onBack={goBack}
              content={content}
            />
          </div>
        }
        {currentId === 'concerns' && (
          <ConcernsScreen
            concerns_config={PCOS_CONCERNS}
            selected={concerns}
            onChange={setConcerns}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {isQuestion && (
          <div  className={styles.cardSep}>
            <div className={styles.cardSepLeft}>
              <QuestionScreen
                key={currentId}
                screenConfig={PCOS_QUESTION_SCREENS[qScreenIdx]}
                answers={answers}
                onChange={handleAnswer}
                onNext={goNext}
                onBack={goBack}
                questionIndex={qScreenIdx}
                totalQuestions={QUESTION_SCREEN_IDS.length}
                qIdxMap={qIdxMapRef}

              />
            </div>
            {showSidebar && (
              <Sidebar steps={PCOS_STEPS} currentStep={sidebarIndex} />
            )}
          </div>
        )}
        {currentId === 'review' && (
          <ReviewScreen
            answers={answers}
            sections={PCOS_REVIEW_SECTIONS}
            onEdit={handleEditSection}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {currentId === 'result' && (
          <ResultScreen answers={answers} onRestart={handleRestart} />
        )}
      </Card>


    </AppShell>
  );
}
